import { useCallback, useEffect, useRef, useState } from "react";

interface VideoBlobFallbackOptions {
  /**
   * When true, `handleError` does NOT download the whole file as a blob — it
   * goes straight to the error state (caller renders a placeholder).
   *
   * Use this for the GRID previews. The blob fallback does a plain `fetch()`
   * (no Range header) which pulls the ENTIRE video into JS memory — up to the
   * 100 MB upload cap. In a grid, a single transient `<video>` error (a cold
   * cache 404, a network blip, mobile data-saver aborting) would spuriously
   * trigger that whole-file download; several videos erroring together is a
   * large, hard-to-diagnose memory/main-thread spike (a reported source of the
   * gallery lag). The grid primary source is the edge-cached CDN URL, so real
   * errors there are rare and a placeholder is an acceptable outcome — the user
   * can still open the lightbox (which keeps the full blob fallback, since only
   * one video is in play there and the user has explicit intent to watch it).
   */
  disableBlobFallback?: boolean;
}

/**
 * Hook that provides automatic blob URL fallback for video playback.
 *
 * Edge's Windows Media Foundation decoder rejects videos served through
 * Next.js API routes (likely due to Transfer-Encoding:chunked / response
 * format issues), even though the bytes decode fine. Chrome tolerates this.
 *
 * This hook:
 * 1. First uses `proxySrc` directly (in practice the caller passes the
 *    edge-cached CDN URL as the primary — fast streaming, no Worker hop)
 * 2. On error, fetches the video as a blob and creates a blob: URL
 * 3. The blob URL bypasses HTTP response format issues entirely
 *
 * Pass `{ disableBlobFallback: true }` for grid previews to skip step 2 — see
 * the option's doc for why the whole-file download must not run in a grid.
 */
export function useVideoBlobFallback(
  proxySrc: string | undefined,
  blobFetchSrc?: string,
  options?: VideoBlobFallbackOptions,
) {
  const disableBlobFallback = options?.disableBlobFallback ?? false;
  const [activeSrc, setActiveSrc] = useState(proxySrc);
  const [error, setError] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  const attemptedFallback = useRef(false);

  // Reset when proxySrc changes
  useEffect(() => {
    // Abort any in-flight fallback fetch
    abortRef.current?.abort();
    // Clean up old blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    attemptedFallback.current = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- source changes must reset the media fallback state.
    setActiveSrc(proxySrc);
    setError(false);
  }, [proxySrc]);

  // Clean up blob URL and abort in-flight fetch on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const abortRef = useRef<AbortController | null>(null);

  const handleError = useCallback(() => {
    // Grid previews opt out of the expensive whole-file blob download — a
    // transient error just shows the placeholder (see option doc).
    if (disableBlobFallback) {
      setError(true);
      return;
    }
    if ((!proxySrc && !blobFetchSrc) || attemptedFallback.current) {
      setError(true);
      return;
    }

    // Abort any in-flight fallback fetch
    abortRef.current?.abort();
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/immutability -- this ref owns the current abort controller for the in-flight request.
    abortRef.current = controller;

    // Try blob URL fallback — use blobFetchSrc (same-origin proxy) if provided,
    // otherwise fall back to proxySrc (may be cross-origin R2 URL)
    const fetchUrl = blobFetchSrc || proxySrc;
    if (!fetchUrl) { setError(true); return; }
    attemptedFallback.current = true;
    fetch(fetchUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (controller.signal.aborted) return;
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;
        setActiveSrc(blobUrl);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(true);
      });
  }, [proxySrc, blobFetchSrc, disableBlobFallback]);

  return { activeSrc, error, handleError };
}
